import { zodResolver } from "@hookform/resolvers/zod"
import {
  createFileRoute,
  isRedirect,
  Link as RouterLink,
  redirect,
  useSearch,
} from "@tanstack/react-router"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import type { Body_login_login_access_token as AccessToken } from "@/client"
import { AuthenticationService, type UserPublic } from "@/client"
import { AuthLayout } from "@/components/Common/AuthLayout"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { LoadingButton } from "@/components/ui/loading-button"
import { PasswordInput } from "@/components/ui/password-input"
import useAuth, { getDefaultRouteForRole } from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"

const formSchema = z.object({
  username: z.email(),
  password: z
    .string()
    .min(1, { message: "Password is required" })
    .min(8, { message: "Password must be at least 8 characters" }),
}) satisfies z.ZodType<AccessToken>

type FormData = z.infer<typeof formSchema>

export const Route = createFileRoute("/login")({
  component: Login,
  beforeLoad: async () => {
    try {
      const user = await AuthenticationService.getCurrentUserInfo()
      const route = getDefaultRouteForRole(user as UserPublic)
      throw redirect({ to: route })
    } catch (e) {
      if (isRedirect(e)) throw e
      // Not authenticated or API error — show login page
    }
  },
  validateSearch: z.object({
    session_expired: z.string().optional(),
  }),
  head: () => ({
    meta: [
      {
        title: "Log In - Broken Islands Lodge",
      },
    ],
  }),
})

/**
 * Render the login page with a validated email/password form and submission handling.
 *
 * Displays a toast when the `session_expired` search parameter is present, validates inputs
 * according to the form schema, and submits credentials via the authentication mutation
 * while preventing duplicate submissions.
 *
 * @returns A React element representing the login page.
 */
function Login() {
  const { loginMutation } = useAuth()
  const { showErrorToast } = useCustomToast()
  const search = useSearch({ from: "/login" })
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      username: "",
      password: "",
    },
  })

  useEffect(() => {
    if (search.session_expired) {
      showErrorToast("Session expired. Please log in again.")
    }
  }, [search.session_expired, showErrorToast])

  const onSubmit = (data: FormData) => {
    if (loginMutation.isPending) return
    loginMutation.mutate(data)
  }

  return (
    <AuthLayout>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">Login to your account</h1>
          </div>

          <div className="grid gap-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      data-testid="email-input"
                      placeholder="user@example.com"
                      type="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center">
                    <FormLabel>Password</FormLabel>
                    <RouterLink
                      to="/recover-password"
                      className="ml-auto text-sm underline-offset-4 hover:underline"
                    >
                      Forgot your password?
                    </RouterLink>
                  </div>
                  <FormControl>
                    <PasswordInput
                      data-testid="password-input"
                      placeholder="Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <LoadingButton type="submit" loading={loginMutation.isPending}>
              Log In
            </LoadingButton>
          </div>
        </form>
      </Form>
    </AuthLayout>
  )
}
